import { ChangeDetectionStrategy, Component } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  ModalService,
  SharedModule,
  TypedBaseListComponent,
} from '@vendure/admin-ui/core';
import { Subject, switchMap } from 'rxjs';

import { RedirectCreateDialogComponent } from './redirect-create-dialog.component';
import {
  REDIRECT_LIST_QUERY,
  type RedirectListQuery,
} from './redirects-admin-api';
import { RedirectsDataService } from './redirects-data.service';

export const REDIRECT_LIST_LOCATION_ID = 'redirect-list';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'redirect-list',
  template: `
    <vdr-page-block>
      <vdr-action-bar>
        <vdr-ab-left> </vdr-ab-left>
        <vdr-ab-right>
          <ng-content select="[ab-right]"></ng-content>
          <button
            class="btn btn-primary ml-1"
            type="button"
            (click)="onClickCreate()"
          >
            <clr-icon shape="plus" />
            {{ 'redirects.create-redirect' | translate }}
          </button>
        </vdr-ab-right>
      </vdr-action-bar>
    </vdr-page-block>

    <vdr-data-table-2
      [id]="REDIRECT_LIST_LOCATION_ID"
      [items]="items$ | async"
      [itemsPerPage]="itemsPerPage$ | async"
      [totalItems]="totalItems$ | async"
      [currentPage]="currentPage$ | async"
      [filters]="filters"
      (pageChange)="setPageNumber($event)"
      (itemsPerPageChange)="setItemsPerPage($event)"
    >
      <vdr-bulk-action-menu
        locationId="redirect-list"
        [hostComponent]="this"
        [selectionManager]="selectionManager"
      />

      <!-- Here we define all the available columns -->
      <vdr-dt2-column
        id="id"
        [heading]="'common.id' | translate"
        [hiddenByDefault]="true"
      >
        <ng-template let-redirect="item">
          {{ redirect.id }}
        </ng-template>
      </vdr-dt2-column>
      <vdr-dt2-column
        id="created-at"
        [heading]="'common.created-at' | translate"
        [hiddenByDefault]="true"
        [sort]="sorts.get('createdAt')"
      >
        <ng-template let-redirect="item">
          {{ redirect.createdAt | localeDate: 'short' }}
        </ng-template>
      </vdr-dt2-column>
      <vdr-dt2-column
        id="updated-at"
        [heading]="'common.updated-at' | translate"
        [hiddenByDefault]="true"
        [sort]="sorts.get('updatedAt')"
      >
        <ng-template let-redirect="item">
          {{ redirect.updatedAt | localeDate: 'short' }}
        </ng-template>
      </vdr-dt2-column>
      <vdr-dt2-column
        id="source"
        heading="Source"
        [optional]="false"
        [sort]="sorts.get('source')"
      >
        <ng-template let-redirect="item">
          <span>{{ redirect.source }}</span>
        </ng-template>
      </vdr-dt2-column>
      <vdr-dt2-column
        id="destination"
        heading="Destination"
        [sort]="sorts.get('destination')"
      >
        <ng-template let-redirect="item">
          <span>{{ redirect.destination }}</span>
        </ng-template>
      </vdr-dt2-column>
      <vdr-dt2-column id="permanent" heading="Permanent">
        <ng-template let-redirect="item">
          <span>
            @if (redirect.permanent) {
              {{ 'common.true' | translate }}
            } @else {
              {{ 'common.false' | translate }}
            }
          </span>
        </ng-template>
      </vdr-dt2-column>
    </vdr-data-table-2>
  `,
  standalone: true,
  imports: [SharedModule],
})
export class RedirectListComponent extends TypedBaseListComponent<
  RedirectListQuery,
  'redirectList'
> {
  // make available in template
  protected readonly REDIRECT_LIST_LOCATION_ID = REDIRECT_LIST_LOCATION_ID;

  readonly filters = this.createFilterCollection()
    .addIdFilter()
    .addDateFilters()
    .addFilter({
      name: 'source',
      type: { kind: 'text' },
      label: 'Source',
      filterField: 'source',
    })
    .addFilter({
      name: 'destination',
      type: { kind: 'text' },
      label: 'Destination',
      filterField: 'destination',
    })
    .connectToRoute(this.route);

  readonly sorts = this.createSortCollection()
    .defaultSort('createdAt', 'DESC')
    .addSort({ name: 'createdAt' })
    .addSort({ name: 'updatedAt' })
    .addSort({ name: 'source' })
    .addSort({ name: 'destination' })
    .connectToRoute(this.route);

  protected onCreateClicked = new Subject<void>();

  constructor(
    modalService: ModalService,
    redirectsDataService: RedirectsDataService,
  ) {
    super();
    super.configure({
      document: REDIRECT_LIST_QUERY,
      getItems: (data) => data.redirectList,
      setVariables: (skip, take) => ({
        options: {
          skip,
          take,
          filter: this.filters.createFilterInput(),
          sort: this.sorts.createSortInput(),
        },
      }),
      refreshListOnChanges: [
        this.filters.valueChanges,
        this.sorts.valueChanges,
        redirectsDataService.refreshRedirectList$,
      ],
    });

    this.onCreateClicked
      .pipe(
        switchMap(() =>
          modalService.fromComponent(RedirectCreateDialogComponent, {
            size: 'md',
            closable: true,
          }),
        ),
        takeUntilDestroyed(),
      )
      .subscribe();
  }

  protected onClickCreate() {
    this.onCreateClicked.next();
  }
}
